import type { ProfileFrame, ProfileSummary } from '@apm/contracts';
import { navigate } from '../router';
import { TIP } from '../terms';
import { formatBytes, formatCount, formatDuration, formatFull, formatMs } from '../time';
import {
  Button,
  Card,
  Cell,
  type Column,
  EmptyState,
  LinkButton,
  Loading,
  Page,
  Row,
  Section,
  Table,
  Tooltip,
  Value,
} from '../ui';
import { useProfileDetail } from './useProfiles';

/**
 * 화면 — CPU 기록 상세 (8회차 결정 4).
 *
 * CPU를 오래 쥐고 있던 함수 표 하나가 전부다. 불꽃 그림은 그리지 않는다.
 * 더 파고들 일이 생기면 "원본 내려받기"로 받아 Chrome 개발자 도구에 넣으면 된다.
 */
const COLUMNS: readonly Column[] = [
  { label: '함수' },
  { label: '자체 시간', width: 'w-32', title: '이 함수 자신이 CPU를 쓴 시간' },
  { label: '비율', width: 'w-52' },
  {
    label: '하위 포함 시간',
    width: 'w-32',
    title: '이 함수가 호출한 함수까지 합쳐 CPU를 쓴 시간',
  },
];

export function ProfileDetailPage({ id }: { id: string }) {
  const { loading, summary, error, stale } = useProfileDetail(id);
  // 다른 기록으로 옮기면 새 응답이 올 때까지 앞 기록을 들고 있다. 앞 주소의 답은 그리지 않는다 (44회차)
  const current = stale ? undefined : summary;

  return (
    <Page>
      <div>
        <Button size="sm" onClick={() => navigate({ name: 'diagnose', sub: 'cpu' })}>
          ← 목록으로
        </Button>
      </div>

      {loading || stale ? <Loading /> : null}
      {error !== undefined ? <Card tone="error">{error}</Card> : null}
      {current ? <Loaded summary={current} /> : null}
    </Page>
  );
}

function Loaded({ summary }: { summary: ProfileSummary }) {
  const { meta, top } = summary;
  const maxPct = top.reduce((max, frame) => Math.max(max, frame.selfPct), 0);

  return (
    <>
      <section className="rounded-xl border border-hair bg-band p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-ink">{formatFull(meta.startedAt)}</h2>
            <p className="mt-1 truncate font-mono text-xs text-muted">
              {meta.service} · {meta.instance}
            </p>
          </div>
          <LinkButton
            href={`/api/profiles/${meta.id}/raw`}
            download={`${meta.id}.cpuprofile`}
            variant="primary"
            title="Chrome 개발자 도구의 Performance 탭에 그대로 넣어 볼 수 있다"
          >
            원본 내려받기
          </LinkButton>
        </div>

        <dl className="tabular mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
          <Value
            label="그때 처리 지연"
            value={<Tooltip text={TIP.loopDelay}>{formatMs(meta.trigger.loopP99Ms)}</Tooltip>}
            tone="text-error"
            hint={`기준 ${meta.trigger.thresholdMs}ms × ${meta.trigger.consecutive}회 연속`}
          />
          <Value label="길이" value={formatDuration(meta.durationMs)} />
          <Value
            label="표본"
            value={`${formatCount(meta.sampleCount)}개`}
            hint={`합계 ${formatDuration(summary.totalMs)}`}
          />
          <Value label="원본 크기" value={formatBytes(meta.sizeBytes)} />
        </dl>
      </section>

      <Section
        boxed
        title={`CPU를 많이 쓴 함수 상위 ${top.length}개`}
        note="자체 시간 내림차순. 위쪽 함수가 지연의 원인일 가능성이 높다"
      >
        {top.length === 0 ? (
          <EmptyState title="표본이 없다" hint="기록하는 동안 CPU가 한가했다" />
        ) : (
          <Table
            columns={COLUMNS}
            caption="CPU를 많이 쓴 함수"
            minWidth="min-w-[780px]"
            maxHeight="max-h-[680px]"
          >
            {top.map((frame) => (
              <FrameRow key={rowKey(frame)} frame={frame} maxPct={maxPct} />
            ))}
          </Table>
        )}
      </Section>
    </>
  );
}

function FrameRow({ frame, maxPct }: { frame: ProfileFrame; maxPct: number }) {
  return (
    <Row>
      <Cell className="max-w-[420px]">
        <p className="truncate font-mono text-xs text-ink" title={frame.fn}>
          {frame.fn}
        </p>
        {frame.url !== undefined ? (
          <p className="truncate text-xs text-muted" title={frame.url}>
            {shortUrl(frame.url)}
            {frame.line === undefined ? '' : `:${frame.line}`}
          </p>
        ) : null}
      </Cell>
      <Cell mono nowrap className="tabular">
        {formatDuration(frame.selfMs)}
      </Cell>
      <Cell>
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="h-1.5 w-full overflow-hidden rounded-full bg-band-strong"
          >
            <span
              className="block h-full rounded-full bg-accent"
              style={{ width: `${maxPct > 0 ? (frame.selfPct / maxPct) * 100 : 0}%` }}
            />
          </span>
          <span className="tabular w-12 shrink-0 text-right text-muted">
            {frame.selfPct.toFixed(1)}%
          </span>
        </span>
      </Cell>
      <Cell mono nowrap tone="muted" className="tabular">
        {formatDuration(frame.totalMs)}
      </Cell>
    </Row>
  );
}

/** 같은 이름·파일·행이면 서버가 이미 한 줄로 합쳤다. 그 셋이 곧 키다 */
function rowKey(frame: ProfileFrame): string {
  return `${frame.fn} ${frame.url ?? ''} ${frame.line ?? ''}`;
}

/** 경로가 길어 화면을 밀어낸다. 앞쪽 스킴과 중간 폴더를 줄인다 */
function shortUrl(url: string): string {
  const withoutScheme = url.replace(/^file:\/\/\/?/, '');
  const parts = withoutScheme.split('/');
  return parts.length <= 3 ? withoutScheme : `…/${parts.slice(-3).join('/')}`;
}
